import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Upload, FileText, CheckCircle, Zap, TrendingUp, ClipboardCheck, Sparkles } from "lucide-react";
import { QuantificationUploadStep } from "@/components/QuantificationWizard/QuantificationUploadStep";
import { QuantificationQuestionsStep } from "@/components/QuantificationWizard/QuantificationQuestionsStep";
import { QuantificationVerifyStep } from "@/components/QuantificationWizard/QuantificationVerifyStep";
import { QuantificationResultsStep } from "@/components/QuantificationWizard/QuantificationResultsStep";

export interface ParsedJob {
  id: string;
  title: string;
  company: string;
  dates: string;
  bullets: string[];
}

export interface MetricQuestion {
  id: string;
  question: string;
  bulletIndex: number;
  bulletText: string;
  metricType: string;
  placeholder: string;
}

export interface QuantificationState {
  parsedJobs: ParsedJob[];
  questions: Record<string, MetricQuestion[]>;
  answers: Record<string, Record<string, string>>; // jobId -> questionId -> answer
  rewrittenBullets: Record<string, Array<{
    bulletIndex: number;
    original: string;
    rewritten: string;
    numberAdded: string | null;
  }>>;
  firstName: string;
  lastName: string;
}

const steps = [
  { id: 1, title: "Upload Resume", icon: Upload },
  { id: 2, title: "Answer Questions", icon: FileText },
  { id: 3, title: "Verify Answers", icon: ClipboardCheck },
  { id: 4, title: "View Results", icon: Sparkles },
];

export default function ResumeQuantification() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [state, setState] = useState<QuantificationState>({
    parsedJobs: [],
    questions: {},
    answers: {},
    rewrittenBullets: {},
    firstName: "",
    lastName: "",
  });

  const progress = ((currentStep - 1) / (steps.length - 1)) * 100;

  const handleUploadComplete = (parsedJobs: ParsedJob[], questions: Record<string, MetricQuestion[]>, firstName: string, lastName: string) => {
    setState(prev => ({ ...prev, parsedJobs, questions, firstName, lastName }));
    setCurrentStep(2);
  };

  const handleQuestionsComplete = (answers: Record<string, Record<string, string>>) => {
    setState(prev => ({ ...prev, answers }));
    setCurrentStep(3);
  };

  const handleVerifyComplete = (rewrittenBullets: Record<string, Array<{
    bulletIndex: number;
    original: string;
    rewritten: string;
    numberAdded: string | null;
  }>>) => {
    setState(prev => ({ ...prev, rewrittenBullets }));
    setCurrentStep(4);
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return <QuantificationUploadStep onComplete={handleUploadComplete} />;
      case 2:
        return (
          <QuantificationQuestionsStep
            parsedJobs={state.parsedJobs}
            questions={state.questions}
            firstName={state.firstName}
            lastName={state.lastName}
            onComplete={handleQuestionsComplete}
            onBack={() => setCurrentStep(1)}
          />
        );
      case 3:
        return (
          <QuantificationVerifyStep
            parsedJobs={state.parsedJobs}
            questions={state.questions}
            answers={state.answers}
            onComplete={handleVerifyComplete}
            onBack={() => setCurrentStep(2)}
          />
        );
      case 4:
        return (
          <QuantificationResultsStep
            parsedJobs={state.parsedJobs}
            questions={state.questions}
            answers={state.answers}
            rewrittenBullets={state.rewrittenBullets}
            firstName={state.firstName}
            lastName={state.lastName}
            onStartOver={() => {
              setState({ parsedJobs: [], questions: {}, answers: {}, rewrittenBullets: {}, firstName: "", lastName: "" });
              setCurrentStep(1);
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-blue-50 dark:bg-slate-700">
      {/* Header */}
      <div className="border-b border-border/40 bg-white dark:bg-gray-900 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
            
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-200 dark:border-orange-800 rounded-full">
              <Zap className="h-4 w-4 text-orange-600 dark:text-orange-400" />
              <span className="text-sm font-medium text-orange-700 dark:text-orange-300">Resume Quantifier</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-5xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl shadow-lg">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                Resume Metrics Wizard
              </h1>
            </div>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Add powerful, meaningful numbers to your resume bullets in minutes
            </p>
          </div>

          {/* Progress Steps */}
          <Card className="mb-8 shadow-lg border border-slate-300 bg-white/70 dark:bg-gray-900/70 backdrop-blur-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl font-semibold">Progress</CardTitle>
                <span className="text-sm text-muted-foreground">Step {currentStep} of {steps.length}</span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex justify-between items-center mb-6">
                {steps.map((step, index) => {
                  const StepIcon = step.icon;
                  const isActive = currentStep === step.id;
                  const isCompleted = currentStep > step.id;
                  
                  return (
                    <div key={step.id} className="flex items-center flex-1">
                      <div className="flex flex-col items-center">
                        <div className={`
                          flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-300 shadow-sm
                          ${isCompleted 
                            ? 'bg-gradient-to-br from-green-500 to-green-600 border-green-500 text-white' 
                            : isActive 
                              ? 'border-primary text-primary bg-primary/10' 
                              : 'border-muted-foreground/30 text-muted-foreground bg-muted/50'}
                        `}>
                          {isCompleted ? <CheckCircle className="h-6 w-6" /> : <StepIcon className="h-6 w-6" />}
                        </div>
                        <p className={`mt-2 text-sm font-medium ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {step.title}
                        </p>
                      </div>
                      {index < steps.length - 1 && (
                        <div className={`flex-1 h-px mx-4 ${isCompleted ? 'bg-green-500' : 'bg-muted-foreground/20'}`} />
                      )}
                    </div>
                  );
                })}
              </div>
              <Progress value={progress} className="h-2" />
            </CardContent>
          </Card>

          {/* Step Content */}
          <Card className="shadow-lg border border-slate-300 bg-white/70 dark:bg-gray-900/70 backdrop-blur-sm">
            <CardContent className="p-8">
              {renderCurrentStep()}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
